import { useEffect, useState } from "react";
import { Minus, Plus } from "lucide-react";
import { api, imgSrc, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const stockStatus = (stock) => {
    if (stock === 0) return { label: "Sold out", cls: "bg-crimson/10 text-crimson" };
    if (stock <= 1) return { label: "Low stock", cls: "bg-gold/20 text-espresso" };
    return { label: "In stock", cls: "bg-green-100 text-green-800" };
};

export const InventoryTab = () => {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = () => {
        api.get("/products")
            .then((r) => setProducts(r.data))
            .catch(() => {})
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        load();
    }, []);

    const updateStock = async (p, value, variantId = null) => {
        const stock = Math.max(0, parseInt(value) || 0);
        if (variantId) {
            const variant = (p.variants || []).find((v) => v.id === variantId);
            if (!variant || stock === variant.stock) return;
            setProducts((prev) => prev.map((x) => {
                if (x.id !== p.id) return x;
                const variants = x.variants.map((v) => (v.id === variantId ? { ...v, stock } : v));
                return { ...x, variants, stock: variants.reduce((s, v) => s + v.stock, 0) };
            }));
        } else {
            if (stock === p.stock) return;
            setProducts((prev) => prev.map((x) => (x.id === p.id ? { ...x, stock } : x)));
        }
        try {
            await api.patch(`/products/${p.id}/stock`, { stock, variant_id: variantId || undefined });
            toast.success(variantId ? `${p.name} — ${variantName(p, variantId)}: ${stock} in stock` : `${p.name}: ${stock} in stock`);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
            load();
        }
    };

    const variantName = (p, variantId) => (p.variants || []).find((v) => v.id === variantId)?.name || "color";

    if (loading) {
        return <p className="font-serif text-lg italic text-clay" data-testid="inventory-loading">Counting stock…</p>;
    }

    const lowCount = products.filter((p) => p.stock <= 1).length;

    const stepper = (value, onMinus, onPlus, onChange, idSuffix) => (
        <div className="flex items-center border border-line">
            <button onClick={onMinus} data-testid={`stock-minus-${idSuffix}`} className="px-2.5 py-2 transition-colors hover:bg-espresso hover:text-sand">
                <Minus size={12} />
            </button>
            <input
                type="number" min="0"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                data-testid={`stock-input-${idSuffix}`}
                className="w-14 border-x border-line py-2 text-center font-mono text-sm outline-none"
            />
            <button onClick={onPlus} data-testid={`stock-plus-${idSuffix}`} className="px-2.5 py-2 transition-colors hover:bg-espresso hover:text-sand">
                <Plus size={12} />
            </button>
        </div>
    );

    return (
        <div data-testid="inventory-tab">
            <div className="mb-6 flex flex-wrap items-center gap-4">
                <p className="font-mono text-xs uppercase tracking-[0.2em] text-clay" data-testid="inventory-summary">
                    {products.length} products · {products.reduce((s, p) => s + p.stock, 0)} pieces in stock
                </p>
                {lowCount > 0 && (
                    <span className="bg-gold/20 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-espresso" data-testid="low-stock-alert">
                        {lowCount} need{lowCount === 1 ? "s" : ""} restocking
                    </span>
                )}
            </div>
            <p className="mb-6 font-mono text-[10px] uppercase tracking-[0.15em] text-clay">Stock automatically decreases after every paid order — sold-out items show a badge and can't be added to bags</p>
            <div className="overflow-x-auto border border-line bg-white">
                <table className="w-full text-left text-sm" data-testid="inventory-table">
                    <thead>
                        <tr className="border-b border-line font-mono text-[10px] uppercase tracking-[0.2em] text-clay">
                            <th className="px-5 py-4">Photo</th>
                            <th className="px-5 py-4">Product</th>
                            <th className="px-5 py-4">Price</th>
                            <th className="px-5 py-4">In stock</th>
                            <th className="px-5 py-4">Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        {products.map((p) => {
                            const s = stockStatus(p.stock);
                            const variants = p.variants || [];
                            return [
                                <tr key={p.id} className={`border-b ${variants.length ? "border-line/50 bg-sand/40" : "border-line"}`} data-testid={`inventory-row-${p.id}`}>
                                    <td className="px-5 py-3">
                                        <div className="h-14 w-11 overflow-hidden bg-line/40">
                                            <img src={imgSrc(p.image)} alt="" className="h-full w-full object-cover" />
                                        </div>
                                    </td>
                                    <td className="px-5 py-3">
                                        <p className="font-medium">{p.name}</p>
                                        <p className="font-mono text-[10px] uppercase tracking-wide text-clay">
                                            {p.category}{variants.length > 0 && ` · ${variants.length} colors`}
                                        </p>
                                    </td>
                                    <td className="px-5 py-3 font-mono text-sm">${p.price.toFixed(2)}</td>
                                    <td className="px-5 py-3">
                                        {variants.length > 0 ? (
                                            <span className="font-mono text-sm" data-testid={`stock-total-${p.id}`}>{p.stock} total</span>
                                        ) : (
                                            stepper(p.stock, () => updateStock(p, p.stock - 1), () => updateStock(p, p.stock + 1), (val) => updateStock(p, val), p.id)
                                        )}
                                    </td>
                                    <td className="px-5 py-3">
                                        <span className={`px-2 py-1 font-mono text-[10px] uppercase tracking-wide ${s.cls}`} data-testid={`stock-status-${p.id}`}>{s.label}</span>
                                    </td>
                                </tr>,
                                ...variants.map((v) => {
                                    const vs = stockStatus(v.stock);
                                    return (
                                        <tr key={`${p.id}-${v.id}`} className="border-b border-line last:border-0" data-testid={`inventory-variant-row-${v.id}`}>
                                            <td className="px-5 py-2 pl-9">
                                                <div className="h-10 w-8 overflow-hidden bg-line/40">
                                                    {v.image ? <img src={imgSrc(v.image)} alt="" className="h-full w-full object-cover" /> : <img src={imgSrc(p.image)} alt="" className="h-full w-full object-cover opacity-40" />}
                                                </div>
                                            </td>
                                            <td className="px-5 py-2">
                                                <p className="font-serif italic text-clay">{v.name}</p>
                                            </td>
                                            <td className="px-5 py-2 font-mono text-xs text-clay">{v.price != null ? `$${v.price.toFixed(2)}` : "base"}</td>
                                            <td className="px-5 py-2">
                                                {stepper(v.stock, () => updateStock(p, v.stock - 1, v.id), () => updateStock(p, v.stock + 1, v.id), (val) => updateStock(p, val, v.id), v.id)}
                                            </td>
                                            <td className="px-5 py-2">
                                                <span className={`px-2 py-1 font-mono text-[10px] uppercase tracking-wide ${vs.cls}`} data-testid={`stock-status-${v.id}`}>{vs.label}</span>
                                            </td>
                                        </tr>
                                    );
                                }),
                            ];
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
};
